// @ts-nocheck
"use client";
import React, { useEffect, useRef, useState } from "react";
import YouTube from "react-youtube";
import Player from "@vimeo/player";
import styles from "./video-embed.module.css";
import { FaCirclePlay } from "react-icons/fa6";
import Link from "next/link";
import Image from "next/image";

interface VideoEmbedProps {
  url: string;
  width?: string | number;
  view?: "view" | "play";
  ctaRedirectURL?: string;
}

const VideoEmbed: React.FC<VideoEmbedProps> = ({
  url,
  width = "100%",
  view = "play",
  ctaRedirectURL = undefined
}) => {
  const isYouTube = url.includes("youtube.com") || url.includes("youtu.be");
  const isVimeo = url.includes("vimeo.com");
  const vimeoRef = useRef<HTMLDivElement | null>(null);
  const [thumbnail, setThumbnail] = useState<string>("");

  const resolvedWidth = typeof width === "number" ? `${width}px` : width;

  // fetch thumbnails if view="view"
  useEffect(() => {
    if (view !== "view") return;

    if (isYouTube) {
      const videoId = url.includes("youtu.be")
        ? url.split("youtu.be/")[1].split("?")[0]
        : new URL(url).searchParams.get("v") || "";
      setThumbnail(`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`);
    }

    if (isVimeo) {
      (async () => {
        const res = await fetch(
          `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`
        );
        const data = await res.json();
        const baseThumb = data.thumbnail_url;
        const hdThumb = baseThumb.replace(/-d_\d+x\d+/, '-d_960x540');
        setThumbnail(hdThumb);
      })();
    }


  }, [url, isYouTube, isVimeo, view]);

  // init vimeo player if playing
  useEffect(() => {
    if (view === "play" && isVimeo && vimeoRef.current) {
      const videoId = new URL(url).pathname.split("/").pop();
      if (!videoId) return;
      vimeoRef.current.innerHTML = "";
      const player = new Player(vimeoRef.current, {
        id: videoId,
        responsive: true,
      });
      return () => {
        player.destroy();
      };
    }
  }, [url, isVimeo, view]);

  if (view === "view" && thumbnail) {
    // render just the thumbnail
    return (
      <div
        className={styles.videoWrapper}
        style={{ width: resolvedWidth, display: "flex", justifyContent: "center" }}
      >
        {ctaRedirectURL && <Link className={styles.playIcon} href={ctaRedirectURL || "#"}>
          <FaCirclePlay />
        </Link>}
        <Image
          src={thumbnail}
          alt="Video thumbnail"
          width={960}
          height={540}
          sizes="(max-width: 768px) 100vw, 960px"
          style={{ width: "100%", height: "auto" }}
          unoptimized
        />
      </div >
    );
  }

  if (view === "play" && isYouTube) {
    const videoId = url.includes("youtu.be")
      ? url.split("youtu.be/")[1].split("?")[0]
      : new URL(url).searchParams.get("v") || "";

    return (
      <div
        className={styles.videoWrapper}
        style={{ width: resolvedWidth, display: "flex", justifyContent: "center" }}
      >
        <YouTube
          videoId={videoId}
          opts={{
            width: resolvedWidth,
            height: "auto",
            playerVars: { autoplay: 0 },
          }}
          style={{ width: resolvedWidth, height: "auto" }}
        />
      </div>
    );
  }

  if (view === "play" && isVimeo) {
    return (
      <div
        className={styles.videoWrapper}
        style={{ width: resolvedWidth, display: "flex", justifyContent: "center" }}
      >
        <div ref={vimeoRef} style={{ width: resolvedWidth, height: "auto" }} />
      </div>
    );
  }

  return null;
};

export default VideoEmbed;
